import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Attempt, AttemptMode, StepWithRegions, Chapter, Region, ClickType } from '@simultrain/shared';
import { evaluateMastery, walkStepChain, extractDisplayText } from '@simultrain/shared';
import { api } from '../api/client.js';
import { useChapterRunner } from '../runner/useChapterRunner.js';
import { useAudioPref, useSpokenAudio } from '../runner/useSpokenAudio.js';
import { useZoomWarning } from '../runner/useZoomWarning.js';

type InputRegion = Extract<Region, { kind: 'input' }>;
type TextRegion = Extract<Region, { kind: 'text' }>;

type ImageMetrics = { drawW: number; drawH: number; offsetX: number; offsetY: number };

// How long (ms) to wait after the cursor arrives before advancing to the next step.
const INSTRUCTION_PAUSE_MS = 2800;
// Duration of the cursor travel animation (must match CSS transition).
const CURSOR_TRAVEL_MS = 700;
// How long (ms) the pointer must rest inside a `hover`-type region before it
// counts as activated — long enough that just passing over it on the way
// somewhere else doesn't trigger it by accident.
const HOVER_DWELL_MS = 500;
// How long (ms) each typed character takes in the instruction-mode typing demo.
const TYPE_CHAR_MS = 70;

const MODE_LABEL: Record<string, string> = {
  instruction: 'Instructie',
  training: 'Training',
  test: 'Toets',
  final_test: 'Eindtoets',
};
const MODE_COLOR: Record<string, string> = {
  instruction: '#0284c7',
  training: '#7c3aed',
  test: '#b45309',
  final_test: '#b45309',
};

// ─── Instruction-mode auto-play loop ──────────────────────────────────────────

type CursorPos = { x: number; y: number }; // pixels within stage-wrap

function useInstructionLoop(
  isInstruction: boolean,
  current: StepWithRegions | null,
  metrics: ImageMetrics,
  imgLoaded: boolean,
  audioRef: React.RefObject<HTMLAudioElement | null>,
  speak: (text: string) => Promise<void>,
  onAdvance: () => void,
) {
  const [cursorPos, setCursorPos] = useState<CursorPos>({ x: -80, y: -80 });
  const [clicking, setClicking] = useState(false);
  // The input region currently being "typed into" for the demo, and how much
  // of its expected value has been revealed so far.
  const [typingRegion, setTypingRegion] = useState<StepWithRegions['regions'][number] | null>(null);
  const [typedText, setTypedText] = useState('');
  const [paused, setPaused] = useState(false);
  const [playFlash, setPlayFlash] = useState(false);
  // Which click the demo cursor is aiming to show off — only meaningful
  // while `clicking` is true; 'left' means no special badge is shown.
  const [demoClickType, setDemoClickType] = useState<ClickType>('left');

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Remembers whichever single timer is "in flight", so pause can freeze the
  // remaining wait and resume can pick up from exactly there.
  const pendingTimerRef = useRef<{ fn: () => void; remaining: number; startedAt: number } | null>(null);
  // Remembers mid-typing progress the same way, for pause/resume.
  const typingStateRef = useRef<{ value: string; index: number } | null>(null);
  const pausedRef = useRef(false);

  // The step only advances once BOTH the narration and a minimum visual
  // pause have completed — whichever takes longer. Refs (not state) because
  // togglePause needs to read/set them outside the per-step effect below.
  const narrationDoneRef = useRef(false);
  const minPauseDoneRef = useRef(false);
  const advancedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current !== null) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const clearTypeInterval = () => {
    if (typeIntervalRef.current !== null) { clearInterval(typeIntervalRef.current); typeIntervalRef.current = null; }
  };

  const maybeAdvance = useCallback(() => {
    if (!advancedRef.current && minPauseDoneRef.current && narrationDoneRef.current) {
      advancedRef.current = true;
      onAdvance();
    }
  }, [onAdvance]);

  // A timeout that pause/resume can freeze and continue later, rather than
  // an ordinary setTimeout that would just keep ticking while paused.
  const schedule = useCallback((fn: () => void, ms: number) => {
    clearTimer();
    pendingTimerRef.current = { fn, remaining: ms, startedAt: Date.now() };
    timerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      fn();
    }, ms);
  }, []);

  const runTyping = useCallback((value: string, fromIndex: number, onDone: () => void) => {
    clearTypeInterval();
    let i = fromIndex;
    typingStateRef.current = { value, index: i };
    typeIntervalRef.current = setInterval(() => {
      i += 1;
      typingStateRef.current = { value, index: i };
      setTypedText(value.slice(0, i));
      if (i >= value.length) {
        clearTypeInterval();
        typingStateRef.current = null;
        onDone();
      }
    }, TYPE_CHAR_MS);
  }, []);

  useEffect(() => {
    if (!isInstruction || !current || !imgLoaded || metrics.drawW === 0) return;
    clearTimer();
    clearTypeInterval();
    setClicking(false);
    setTypingRegion(null);
    setTypedText('');
    setDemoClickType('left');
    setPaused(false);
    pausedRef.current = false;
    pendingTimerRef.current = null;
    typingStateRef.current = null;
    narrationDoneRef.current = false;
    minPauseDoneRef.current = false;
    advancedRef.current = false;

    if (current.autoAdvanceMs != null) {
      // Auto-advance ("loading screen") steps are handled by the unified
      // auto-advance effect in ChapterRunnerPage, not by this cursor demo.
      setCursorPos({ x: -80, y: -80 });
      return;
    }

    let cancelled = false;
    void speak(current.instruction).then(() => {
      if (cancelled) return;
      narrationDoneRef.current = true;
      maybeAdvance();
    });

    // Find the first click-correct (or input) region to animate toward. When
    // a step has several valid ways to succeed, only the one(s) authored
    // with showInInstruction (default true) are candidates — the rest stay
    // usable in training/test, just not acted out here.
    const region = current.regions.find(
      (r) => (r.kind === 'click-correct' && r.showInInstruction !== false) || r.kind === 'input',
    );
    if (!region) {
      // No click target on this step — just pause and advance.
      schedule(() => { minPauseDoneRef.current = true; maybeAdvance(); }, INSTRUCTION_PAUSE_MS);
      return () => { cancelled = true; clearTimer(); clearTypeInterval(); };
    }

    const targetX = metrics.offsetX + (region.rect.x + region.rect.w / 2) * metrics.drawW;
    const targetY = metrics.offsetY + (region.rect.y + region.rect.h / 2) * metrics.drawH;

    setDemoClickType(region.kind === 'click-correct' ? region.clickType ?? 'left' : 'left');

    // Move cursor to target.
    setCursorPos({ x: targetX, y: targetY });

    // After travel completes: show click pulse.
    schedule(() => {
      setClicking(true);

      const literalValue = region.kind === 'input' && region.expected.kind === 'literal' ? region.expected.value : '';
      if (literalValue) {
        // Type the expected value character by character, then pause and advance.
        schedule(() => {
          setClicking(false);
          setTypingRegion(region);
          runTyping(literalValue, 0, () => {
            schedule(() => { minPauseDoneRef.current = true; maybeAdvance(); }, INSTRUCTION_PAUSE_MS);
          });
        }, 200);
      } else {
        // No literal value to demo (click-correct, or a regex-based input) —
        // just pause and advance.
        schedule(() => {
          setClicking(false);
          minPauseDoneRef.current = true;
          maybeAdvance();
        }, INSTRUCTION_PAUSE_MS);
      }
    }, CURSOR_TRAVEL_MS + 100);

    return () => { cancelled = true; clearTimer(); clearTypeInterval(); };
  }, [isInstruction, current?.id, imgLoaded, metrics.drawW, metrics.offsetX, metrics.offsetY]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearTimer(); clearTypeInterval(); if (playFlashTimerRef.current) clearTimeout(playFlashTimerRef.current); }, []);

  const togglePause = useCallback(() => {
    if (!isInstruction) return;
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);

    if (next) {
      // Freeze whichever single timer/typing sequence is currently running,
      // remembering exactly how much was left — resume must not restart it.
      if (timerRef.current !== null && pendingTimerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        pendingTimerRef.current.remaining = Math.max(
          0,
          pendingTimerRef.current.remaining - (Date.now() - pendingTimerRef.current.startedAt),
        );
      }
      if (typeIntervalRef.current !== null) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
      audioRef.current?.pause();
    } else {
      if (pendingTimerRef.current) {
        const { fn, remaining } = pendingTimerRef.current;
        pendingTimerRef.current = { fn, remaining, startedAt: Date.now() };
        timerRef.current = setTimeout(() => { pendingTimerRef.current = null; fn(); }, remaining);
      } else if (typingStateRef.current) {
        const { value, index } = typingStateRef.current;
        runTyping(value, index, () => {
          schedule(() => { minPauseDoneRef.current = true; maybeAdvance(); }, INSTRUCTION_PAUSE_MS);
        });
      }
      // Native <audio> resumes from exactly where .pause() left it — no need
      // to re-fetch or reset currentTime.
      void audioRef.current?.play().catch(() => {});

      setPlayFlash(true);
      if (playFlashTimerRef.current) clearTimeout(playFlashTimerRef.current);
      playFlashTimerRef.current = setTimeout(() => setPlayFlash(false), 800);
    }
  }, [isInstruction, audioRef, schedule, runTyping, maybeAdvance]);

  return { cursorPos, clicking, demoClickType, typingRegion, typedText, paused, playFlash, togglePause };
}

// ─── Main component ───────────────────────────────────────────────────────────

// Leave the runner: if this tab was opened via window.open (e.g. the Studio's
// preview buttons), there's no meaningful browser history to go "back" to —
// close the tab instead. Otherwise return to the trainings list.
function exitRunner(nav: ReturnType<typeof useNavigate>) {
  if (window.opener) {
    window.close();
  } else {
    nav('/');
  }
}

type ChapterRunnerProps = {
  // Set only when reached via a share link (see SharedChapterPage) — falls
  // back to the route params otherwise, so the normal /run/:chapterId/:mode
  // path is completely unaffected.
  chapterId?: string;
  mode?: AttemptMode;
  shareToken?: string;
};

export function ChapterRunnerPage(props: ChapterRunnerProps = {}) {
  const params = useParams<{ chapterId: string; mode: AttemptMode }>();
  const chapterId = props.chapterId ?? params.chapterId;
  const mode = props.mode ?? params.mode;
  const { shareToken } = props;
  const nav = useNavigate();
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [steps, setSteps] = useState<StepWithRegions[]>([]);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [metrics, setMetrics] = useState<ImageMetrics>({ drawW: 0, drawH: 0, offsetX: 0, offsetY: 0 });
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [stoppedEarly, setStoppedEarly] = useState(false);
  const [ttsAudioMap, setTtsAudioMap] = useState<Record<string, string>>({});
  const [showStepList, setShowStepList] = useState(false);
  const [zoomWarningDismissed, setZoomWarningDismissed] = useState(false);
  // The `hover`-type region currently under the pointer, if any — drives the
  // grey highlight while its dwell timer is running (see onStageMouseMove).
  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // An input's onBlur fires before the same click's onClick bubbles to the
  // stage — set when blur already confirmed the field, so that same click
  // landing on empty stage (or another region) isn't ALSO scored as a miss/hit.
  const suppressStageClickRef = useRef(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Which region id (or null) the pending hover timer above belongs to —
  // lets onStageMouseMove tell "still dwelling on the same region" apart
  // from "moved to a different one", without that check re-running on every
  // mousemove pixel (only the identity of the hit region matters).
  const hoverRegionIdRef = useRef<string | null>(null);
  // Wall-clock timestamp (performance.now()) the current hover session
  // started — see the wall-clock fallback check in onStageMouseMove for why
  // this exists alongside hoverTimerRef.
  const hoverEnteredAtRef = useRef<number | null>(null);
  const zoomedDifferently = useZoomWarning();

  const isInstruction = mode === 'instruction';
  const isTestLike = mode === 'test' || mode === 'final_test';

  const runner = useChapterRunner(steps, chapter?.startStepId ?? null, mode ?? 'instruction');

  const hasAnyAudio = Object.keys(ttsAudioMap).length > 0;
  const [audioPref, setAudioPref] = useAudioPref(hasAnyAudio);
  const assetUrl = useCallback(
    (id: string) => (shareToken ? api.sharedAssetUrl(shareToken, id) : api.assetUrl(id)),
    [shareToken],
  );
  const { audioRef, speak, replay } = useSpokenAudio(ttsAudioMap, audioPref, assetUrl);

  // Re-arm the warning if the learner zooms again after dismissing it once.
  useEffect(() => { if (!zoomedDifferently) setZoomWarningDismissed(false); }, [zoomedDifferently]);

  // Best-effort only: on macOS, mousemove is never delivered to a window
  // that isn't the active OS application (confirmed via a debug build — 0
  // mousemove events reached the page until an actual app switch, despite
  // document.hasFocus() already reporting true throughout). window.focus()
  // cannot force that — browsers deliberately don't let a script raise
  // itself above whatever application the user is actually using, so a
  // popup opened via the Studio's preview buttons can sit there fully
  // "focused" from the DOM's point of view and still never see the pointer
  // move. Every click-based region survives this by accident (the first
  // click that activates the window is still delivered), but `hover` has no
  // click to spend, so the `hasHoverRegion` hint below covers the rest: tell
  // the learner to click the window once.
  useEffect(() => { window.focus(); }, []);

  // In instruction mode the runner advances programmatically, not via clicks.
  const advanceInstruction = useCallback(() => {
    if (!runner.current) return;
    const next = runner.current.successNext;
    runner.instructionAdvance(next);
  }, [runner]);

  // Input fields are separate DOM elements, not part of the click hit-test —
  // their typed value resets whenever the current step changes.
  useEffect(() => {
    setInputValues({});
  }, [runner.current?.id]);

  // A pending hover-dwell session belongs to the step it was armed on —
  // moving to a new step (e.g. because the dwell itself just fired) must not
  // leave it running or its highlight stuck.
  useEffect(() => {
    return () => clearHoverTracking();
  }, [runner.current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitInputRegion = useCallback(
    (region: InputRegion) => {
      runner.submitInput(region, inputValues[region.id] ?? '');
    },
    [runner, inputValues],
  );

  useEffect(() => {
    if (!chapterId || !mode) return;
    void (async () => {
      const data = shareToken
        ? await api.getSharedChapterFull(shareToken)
        : await api.getChapterFull(chapterId);
      setChapter(data.chapter);
      setSteps(data.steps);
      setTtsAudioMap(data.ttsAudio);
      // A shared view never starts an attempt — there's no scoring/tracking
      // for an anonymous or not-necessarily-enrolled visitor (see plan).
      if (!shareToken) {
        const a = await api.startAttempt(chapterId, mode);
        setAttempt(a);
      }
    })();
  }, [chapterId, mode, shareToken]);

  // Load screenshot via authenticated fetch.
  useEffect(() => {
    setImgLoaded(false);
    if (!runner.current?.screenshotAssetId) { setImgUrl(null); return; }
    let active = true;
    let objUrl: string | null = null;
    void (async () => {
      const token = JSON.parse(localStorage.getItem('simultrain-player-auth') ?? '{}')?.state?.accessToken;
      const r = await fetch(assetUrl(runner.current!.screenshotAssetId!), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok || !active) return;
      const blob = await r.blob();
      objUrl = URL.createObjectURL(blob);
      if (active) setImgUrl(objUrl);
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [runner.current?.screenshotAssetId, assetUrl]);

  // Recompute natural→draw mapping on resize or image load.
  useEffect(() => {
    function update() {
      if (!stageRef.current || !imgRef.current) return;
      const stage = stageRef.current.getBoundingClientRect();
      const img = imgRef.current;
      const ratioImg = img.naturalWidth / img.naturalHeight;
      const ratioStage = stage.width / stage.height;
      let drawW: number, drawH: number;
      if (ratioImg > ratioStage) { drawW = stage.width; drawH = stage.width / ratioImg; }
      else { drawH = stage.height; drawW = stage.height * ratioImg; }
      setMetrics({ drawW, drawH, offsetX: (stage.width - drawW) / 2, offsetY: (stage.height - drawH) / 2 });
    }
    const ro = new ResizeObserver(update);
    if (stageRef.current) ro.observe(stageRef.current);
    if (imgRef.current?.complete) { update(); setImgLoaded(true); }
    const el = imgRef.current;
    const onLoad = () => { update(); setImgLoaded(true); };
    el?.addEventListener('load', onLoad);
    return () => { ro.disconnect(); el?.removeEventListener('load', onLoad); };
  }, [imgUrl]);

  const { cursorPos, clicking, demoClickType, typingRegion, typedText, paused, playFlash, togglePause } = useInstructionLoop(
    isInstruction,
    runner.current,
    metrics,
    imgLoaded,
    audioRef,
    speak,
    advanceInstruction,
  );

  // Spacebar toggles pause/resume during instruction playback.
  useEffect(() => {
    if (!isInstruction) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        togglePause();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isInstruction, togglePause]);

  // A region's clickType only constrains click-correct/click-error hits —
  // input/drag-source regions aren't affected by click type at all (input has
  // its own confirm mechanism; drag-source isn't click-driven), so they only
  // ever participate in a plain left click, matching pre-existing behaviour.
  function resolveStageInteraction(clientX: number, clientY: number, clickType: ClickType) {
    if (!runner.current) return;
    if (runner.current.autoAdvanceMs != null) return; // inert screen — ignore all input
    const stage = stageRef.current!.getBoundingClientRect();
    const px = clientX - stage.left - metrics.offsetX;
    const py = clientY - stage.top - metrics.offsetY;
    if (px < 0 || py < 0 || px > metrics.drawW || py > metrics.drawH) { runner.handleMiss(); return; }
    const nx = px / metrics.drawW;
    const ny = py / metrics.drawH;
    const hit = runner.current.regions.find((r) => {
      if (r.kind === 'text') return false; // decorative caption, never a click target
      if (nx < r.rect.x || nx > r.rect.x + r.rect.w || ny < r.rect.y || ny > r.rect.y + r.rect.h) return false;
      if (r.kind === 'click-correct' || r.kind === 'click-error') return (r.clickType ?? 'left') === clickType;
      return clickType === 'left';
    });
    if (hit) runner.handleHit(hit); else runner.handleMiss();
  }

  // A real double-click fires click, click, dblclick in that order — a naive
  // click handler would process the first click (as a plain left click)
  // before the dblclick ever arrives. Defer briefly so a following dblclick
  // can cancel it and take over instead.
  const singleClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (singleClickTimerRef.current) clearTimeout(singleClickTimerRef.current); };
  }, [runner.current?.id]);

  function onStageClick(e: React.MouseEvent) {
    if (suppressStageClickRef.current) { suppressStageClickRef.current = false; return; }
    if (isInstruction) { togglePause(); return; } // click anywhere pauses/resumes
    if (singleClickTimerRef.current) clearTimeout(singleClickTimerRef.current);
    const { clientX, clientY } = e;
    singleClickTimerRef.current = setTimeout(() => {
      singleClickTimerRef.current = null;
      resolveStageInteraction(clientX, clientY, 'left');
    }, 280);
  }

  function onStageDoubleClick(e: React.MouseEvent) {
    if (isInstruction) return;
    if (singleClickTimerRef.current) { clearTimeout(singleClickTimerRef.current); singleClickTimerRef.current = null; }
    resolveStageInteraction(e.clientX, e.clientY, 'double');
  }

  function onStageContextMenu(e: React.MouseEvent) {
    e.preventDefault(); // suppress the native browser context menu
    if (isInstruction) return;
    resolveStageInteraction(e.clientX, e.clientY, 'right');
  }

  // Separate from resolveStageInteraction's hit-test: this one runs off a
  // continuous mousemove rather than a single discrete event, and only ever
  // looks for `hover`-type regions — a plain click never satisfies one (see
  // resolveStageInteraction's clickType match) and a hover never satisfies a
  // click-type one, so the two hit-tests don't need to agree on anything but
  // geometry.
  function findHoverRegion(clientX: number, clientY: number): Region | null {
    if (!runner.current || runner.current.autoAdvanceMs != null) return null;
    const stage = stageRef.current!.getBoundingClientRect();
    const px = clientX - stage.left - metrics.offsetX;
    const py = clientY - stage.top - metrics.offsetY;
    if (px < 0 || py < 0 || px > metrics.drawW || py > metrics.drawH) return null;
    const nx = px / metrics.drawW;
    const ny = py / metrics.drawH;
    return (
      runner.current.regions.find((r) => {
        if (r.kind !== 'click-correct' && r.kind !== 'click-error') return false;
        if ((r.clickType ?? 'left') !== 'hover') return false;
        return nx >= r.rect.x && nx <= r.rect.x + r.rect.w && ny >= r.rect.y && ny <= r.rect.y + r.rect.h;
      }) ?? null
    );
  }

  function clearHoverTracking() {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    hoverRegionIdRef.current = null;
    hoverEnteredAtRef.current = null;
    setHoveredRegionId(null);
  }

  function fireHover(hit: Region) {
    clearHoverTracking();
    runner.handleHit(hit);
  }

  function onStageMouseMove(e: React.MouseEvent) {
    if (isInstruction) return; // the demo drives its own cursor, not a real one
    const hit = findHoverRegion(e.clientX, e.clientY);
    const hitId = hit?.id ?? null;

    if (hitId !== hoverRegionIdRef.current) {
      // Entering a new region (or leaving one) — (re)start tracking.
      if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
      hoverRegionIdRef.current = hitId;
      setHoveredRegionId(hitId);
      hoverEnteredAtRef.current = hit ? performance.now() : null;
      if (hit) {
        hoverTimerRef.current = setTimeout(() => { hoverTimerRef.current = null; fireHover(hit); }, HOVER_DWELL_MS);
      }
      return;
    }

    // Still on the same region — a wall-clock fallback alongside the timer
    // above, in case this tab was throttled while backgrounded.
    if (hit && hoverEnteredAtRef.current != null && performance.now() - hoverEnteredAtRef.current >= HOVER_DWELL_MS) {
      fireHover(hit);
    }
  }

  function onStageMouseLeave() {
    clearHoverTracking();
  }

  // Auto-advance ("loading screen") steps aren't a task the learner completes —
  // exclude them from the progress denominator and from mastery evaluation.
  const mainSteps = steps.filter(
    (s) => (s.stepKind === 'main' || s.stepKind == null) && s.autoAdvanceMs == null,
  );
  const mastery = isTestLike && chapter
    ? evaluateMastery(runner.events, mainSteps.length, chapter.masteryThreshold)
    : null;
  const isFinished = runner.finished || stoppedEarly;

  // Instruction mode: the happy-flow chain, in true order (never raw array
  // order — see walkStepChain), skipping loading screens — there's nothing
  // to review there. Powers "vorige/volgende" and the step-jump list.
  const browsableSteps = walkStepChain(steps, chapter?.startStepId ?? null).filter(
    (s) => s.autoAdvanceMs == null,
  );
  const browseIndex = runner.current ? browsableSteps.findIndex((s) => s.id === runner.current!.id) : -1;
  function goToBrowseIndex(idx: number) {
    const target = browsableSteps[idx];
    if (target) runner.instructionAdvance(target.id);
    setShowStepList(false);
  }

  useEffect(() => {
    if (!isFinished || !attempt) return;
    const status = isTestLike ? (mastery?.passed ? 'passed' : 'failed') : 'passed';
    void api.finishAttempt(attempt.id, status, runner.events).catch(() => {});
  }, [isFinished, attempt, runner.events, isTestLike, mastery?.passed]);

  // Caption — steady "what to do" text (must be computed null-safe here,
  // before the early returns below, since hooks can't be called
  // conditionally). Kept raw (markup included) for speak() / hashing —
  // display strips the `{display|spoken}` TTS markup via extractDisplayText.
  const rawCaptionText = !chapter
    ? ''
    : isTestLike
      ? chapter.title
      : isInstruction
        ? runner.current?.instruction ?? ''
        : runner.current?.taskPrompt || runner.current?.instruction || '';
  const captionText = extractDisplayText(rawCaptionText);
  const rawFeedbackText = runner.feedback.kind !== 'idle' ? runner.feedback.text : '';
  const feedbackText = extractDisplayText(rawFeedbackText);

  // Instruction mode narrates via useInstructionLoop itself (which also
  // waits for narration to finish before advancing), and an auto-advance
  // ("loading screen") step is narrated by the effect below instead (which
  // also owns its timing) — don't double-speak here in either case.
  const isAutoAdvance = runner.current?.autoAdvanceMs != null;
  useEffect(() => {
    if (!isInstruction && !isAutoAdvance) void speak(rawCaptionText);
  }, [rawCaptionText, isInstruction, isAutoAdvance]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isInstruction && rawFeedbackText) void speak(rawFeedbackText); }, [rawFeedbackText, isInstruction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance ("loading screen") steps are inert to all input — they play
  // the caption narration (if any, in every mode, unlike the
  // instruction-only cursor demo above) and wait for at least autoAdvanceMs
  // before moving to successNext (or ending the chapter if that's null).
  // Narration wins when it's the longer of the two — cutting it off early
  // just because the configured duration was set too short would be worse
  // than a slightly longer loading screen.
  useEffect(() => {
    const ms = runner.current?.autoAdvanceMs;
    if (ms == null) return;
    const next = runner.current!.successNext;
    let cancelled = false;
    let minWaitTimer: ReturnType<typeof setTimeout>;
    const minWait = new Promise<void>((resolve) => { minWaitTimer = setTimeout(resolve, ms); });
    void Promise.all([speak(rawCaptionText), minWait]).then(() => {
      if (!cancelled) runner.instructionAdvance(next);
    });
    return () => { cancelled = true; clearTimeout(minWaitTimer); };
  }, [runner.current?.id, runner.current?.autoAdvanceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chapter) return <div className="empty">…</div>;

  // ── Finished screen ──
  if (isFinished) {
    const firstCount = runner.events.filter((e) => e.outcome === 'correct_first_try').length;
    const total = new Set(runner.events.map((e) => e.stepId)).size;
    const selfCorrected = runner.events.filter((e) => e.outcome === 'self_corrected').length;
    const feedbackRecovered = runner.events.filter((e) => e.outcome === 'feedback_recovered').length;
    const wrongCount = runner.events.filter((e) => e.outcome === 'wrong').length;
    const pct = total > 0 ? Math.round((firstCount / total) * 100) : 100;
    const passed = isTestLike ? (mastery?.passed ?? false) : null;

    return (
      <div className="runner-finished">
        <div className="finished-card">
          <div className={`finished-icon ${passed === false ? 'fail' : ''}`}>{passed === false ? '✕' : '✓'}</div>
          <h2>{chapter.title}</h2>
          <p className="muted" style={{ marginBottom: 24 }}>
            {MODE_LABEL[mode ?? 'instruction']} {stoppedEarly ? 'gestopt' : 'afgerond'}
            {passed !== null && (passed ? ' — geslaagd' : ' — niet geslaagd')}
          </p>
          <div className="score-ring">
            <svg viewBox="0 0 100 100" width="120" height="120">
              <circle cx="50" cy="50" r="42" fill="none" stroke="var(--border)" strokeWidth="10" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={passed === false ? 'var(--danger)' : 'var(--success)'} strokeWidth="10"
                strokeDasharray={`${2 * Math.PI * 42}`}
                strokeDashoffset={`${2 * Math.PI * 42 * (1 - pct / 100)}`}
                strokeLinecap="round" transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
              <text x="50" y="55" textAnchor="middle" fontSize="22" fill="var(--text)" fontWeight="bold">{pct}%</text>
            </svg>
          </div>
          {!isInstruction && (
            <div className="score-details">
              <div className="score-row"><span className="score-label">Eerste keer goed</span><span className="score-value good">{firstCount}</span></div>
              {selfCorrected > 0 && <div className="score-row"><span className="score-label">Zelf gecorrigeerd</span><span className="score-value warn">{selfCorrected}</span></div>}
              {feedbackRecovered > 0 && <div className="score-row"><span className="score-label">Via zijstap hersteld</span><span className="score-value warn">{feedbackRecovered}</span></div>}
              {wrongCount > 0 && <div className="score-row"><span className="score-label">Fout</span><span className="score-value bad">{wrongCount}</span></div>}
              <div className="score-row total"><span className="score-label">Totaal stappen</span><span className="score-value">{total}</span></div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 32, justifyContent: 'center' }}>
            {passed === false && (
              <button onClick={() => nav(`/run/${chapterId}/training`)}>Oefen dit hoofdstuk in Training</button>
            )}
            <button className="secondary" onClick={() => exitRunner(nav)}>Sluiten</button>
          </div>
        </div>
      </div>
    );
  }

  if (!runner.current) return <div className="empty">…</div>;

  const currentMode = mode ?? 'instruction';
  const currentStepScored = runner.events.some((e) => e.stepId === runner.current!.id);
  const completedCount = new Set(runner.events.map((e) => e.stepId)).size;
  const progressCurrent = currentStepScored ? completedCount : completedCount + 1;
  const progressTotal = mainSteps.length;

  const feedbackKind = runner.feedback.kind;
  // Avoid covering a region near the top of the screenshot.
  const balloonPosition = runner.current.regions.some((r) => r.rect.y < 0.22) ? 'bottom' : 'top';

  const belowThreshold = isTestLike && mastery !== null && !mastery.stillPassable;

  const hasHoverRegion = runner.current.regions.some(
    (r) => (r.kind === 'click-correct' || r.kind === 'click-error') && (r.clickType ?? 'left') === 'hover',
  );

  return (
    <div className={`runner ${belowThreshold ? 'below-threshold' : ''}`} style={{ cursor: isInstruction || runner.current.autoAdvanceMs != null ? 'default' : 'crosshair' }}>
      <audio ref={audioRef} hidden />
      {/* Top bar */}
      <div className="runner-topbar">
        <div className="mode-pill" style={{ background: MODE_COLOR[currentMode] ?? 'var(--panel-2)' }}>
          {MODE_LABEL[currentMode] ?? currentMode}
        </div>
        <div className="progress-bar-wrap">
          <div className="progress-bar-fill"
            style={{ width: `${progressTotal > 0 ? (progressCurrent / progressTotal) * 100 : 0}%` }} />
        </div>
        <div className="progress-label">{progressCurrent}/{progressTotal}</div>
        {isInstruction && (
          <div className="row instruction-nav" style={{ position: 'relative' }}>
            <button
              className="secondary"
              onClick={() => goToBrowseIndex(browseIndex - 1)}
              disabled={browseIndex <= 0}
              style={{ padding: '5px 10px', fontSize: 13 }}
              title="Vorige stap"
            >
              ◀
            </button>
            <button
              className="secondary"
              onClick={() => setShowStepList((v) => !v)}
              style={{ padding: '5px 10px', fontSize: 13 }}
              title="Alle stappen"
            >
              ☰
            </button>
            <button
              className="secondary"
              onClick={() => goToBrowseIndex(browseIndex + 1)}
              disabled={browseIndex < 0 || browseIndex >= browsableSteps.length - 1}
              style={{ padding: '5px 10px', fontSize: 13 }}
              title="Volgende stap"
            >
              ▶
            </button>
            {showStepList && (
              <div className="step-jump-panel">
                {browsableSteps.map((s, idx) => (
                  <div
                    key={s.id}
                    className={`step-jump-item ${s.id === runner.current?.id ? 'active' : ''}`}
                    onClick={() => goToBrowseIndex(idx)}
                  >
                    {idx + 1}. {s.instruction || s.taskPrompt || `Stap ${idx + 1}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {hasAnyAudio && (
          <div className="row audio-controls">
            <select
              value={audioPref}
              onChange={(e) => setAudioPref(e.target.value as typeof audioPref)}
              style={{ width: 'auto', fontSize: 13, padding: '5px 8px' }}
              title="Tekst en/of audio"
            >
              <option value="text">Tekst</option>
              <option value="audio">Audio</option>
              <option value="both">Beide</option>
            </select>
            {audioPref !== 'text' && (
              <button className="secondary" onClick={replay} style={{ padding: '5px 10px', fontSize: 13 }} title="Opnieuw afspelen">
                🔊
              </button>
            )}
          </div>
        )}
        {belowThreshold && (
          <button className="danger" onClick={() => setStoppedEarly(true)} style={{ padding: '5px 12px', fontSize: 13 }}>
            Toets stoppen
          </button>
        )}
        <button className="secondary" onClick={() => exitRunner(nav)} style={{ padding: '5px 12px', fontSize: 13 }}>
          Stoppen
        </button>
      </div>

      {zoomedDifferently && !zoomWarningDismissed && (
        <div className="zoom-warning">
          Je browserzoom staat niet op 100%. Hierdoor kunnen kaders verschoven lijken — zet de zoom terug (Ctrl/Cmd+0) voor een juiste weergave.
          <button className="secondary" onClick={() => setZoomWarningDismissed(true)} title="Verbergen">×</button>
        </div>
      )}

      {/* Instruction/task caption — above the stage, read this first. */}
      <div className="subtitle-bar">
        <div className="subtitle-text">{captionText}</div>
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        className="stage-wrap"
        onClick={onStageClick}
        onDoubleClick={onStageDoubleClick}
        onContextMenu={onStageContextMenu}
        onMouseMove={onStageMouseMove}
        onMouseLeave={onStageMouseLeave}
      >
        {imgUrl
          ? <img ref={imgRef} src={imgUrl} alt="" className="stage-img" draggable={false} />
          : <div className="muted">geen screenshot</div>}

        {/* Freely placed caption/annotation boxes — same in every mode,
            purely decorative (never a click target, see resolveStageInteraction).
            Gated on imgLoaded for the same reason as the overlays below. */}
        {imgLoaded && runner.current.regions
          .filter((r): r is TextRegion => r.kind === 'text')
          .map((r) => (
            <div
              key={r.id}
              className={`text-region ${r.shadow ? 'shadow' : ''}`}
              style={{
                left: metrics.offsetX + r.rect.x * metrics.drawW,
                top: metrics.offsetY + r.rect.y * metrics.drawH,
                width: r.rect.w * metrics.drawW,
                height: r.rect.h * metrics.drawH,
                fontSize: r.fontSize,
                color: r.textColor,
                background: r.backgroundColor,
                borderColor: r.borderColor,
              }}
            >
              {r.text}
            </div>
          ))}

        {/* Instruction: pulsing overlay on the target region. Gated on
            imgLoaded — regions update the instant the step changes, but the
            screenshot itself only swaps in once its (async, auth'd) fetch
            resolves; without this the overlay jumps to the new step's
            position while the OLD screenshot is still showing underneath it. */}
        {isInstruction && imgLoaded && runner.current.regions
          .filter((r) => (r.kind === 'click-correct' && r.showInInstruction !== false) || r.kind === 'input')
          .map((r) => (
            <div key={r.id} className="region-overlay" style={{
              left: metrics.offsetX + r.rect.x * metrics.drawW,
              top: metrics.offsetY + r.rect.y * metrics.drawH,
              width: r.rect.w * metrics.drawW,
              height: r.rect.h * metrics.drawH,
            }} />
          ))}

        {/* Stage-3 escalation on a mistake without its own corrective path:
            reveal the correct region, same highlight as instruction mode. */}
        {!isInstruction && imgLoaded && runner.revealRegionId && runner.current.regions
          .filter((r) => r.id === runner.revealRegionId)
          .map((r) => (
            <div key={r.id} className="region-overlay" style={{
              left: metrics.offsetX + r.rect.x * metrics.drawW,
              top: metrics.offsetY + r.rect.y * metrics.drawH,
              width: r.rect.w * metrics.drawW,
              height: r.rect.h * metrics.drawH,
            }} />
          ))}

        {/* Training/test: a `hover`-type region under the pointer gets a grey
            highlight for its dwell duration — the closest a static screenshot
            can get to the real menu-item highlight it's standing in for. */}
        {!isInstruction && imgLoaded && hoveredRegionId && runner.current.regions
          .filter((r) => r.id === hoveredRegionId)
          .map((r) => (
            <div
              key={r.id}
              className="region-hover-overlay"
              style={{
                left: metrics.offsetX + r.rect.x * metrics.drawW,
                top: metrics.offsetY + r.rect.y * metrics.drawH,
                width: r.rect.w * metrics.drawW,
                height: r.rect.h * metrics.drawH,
                ['--hover-dwell-ms' as string]: `${HOVER_DWELL_MS}ms`,
              }}
            />
          ))}

        {/* Training/test: a real input field over each input-region, so the
            learner can actually type. Which of Enter/Tab/blur/button confirm
            it is per-region (regions.confirmTriggers), authored in the Studio —
            older regions predating that field fall back to Enter+Tab. */}
        {!isInstruction && imgLoaded && runner.current.autoAdvanceMs == null && runner.current.regions
          .filter((r): r is InputRegion => r.kind === 'input')
          .map((r, idx) => {
            const value = inputValues[r.id] ?? '';
            const confirmTriggers = r.confirmTriggers ?? ['enter', 'tab'];
            return (
              <div key={r.id} style={{ display: 'contents' }}>
                <input
                  key={`${runner.current!.id}-${r.id}`}
                  className="input-region-field"
                  style={{
                    left: metrics.offsetX + r.rect.x * metrics.drawW,
                    top: metrics.offsetY + r.rect.y * metrics.drawH,
                    width: r.rect.w * metrics.drawW,
                    height: r.rect.h * metrics.drawH,
                    ...(r.textColor ? { color: r.textColor } : {}),
                  }}
                  value={value}
                  placeholder={r.placeholder}
                  autoFocus={idx === 0}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setInputValues((v) => ({ ...v, [r.id]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && confirmTriggers.includes('enter')) {
                      e.preventDefault();
                      submitInputRegion(r);
                    } else if (e.key === 'Tab' && confirmTriggers.includes('tab')) {
                      e.preventDefault();
                      submitInputRegion(r);
                    }
                  }}
                  onBlur={(e) => {
                    if (!confirmTriggers.includes('blur') || value.trim() === '') return;
                    // Focus moving straight to the confirm button means that
                    // button's own onClick will submit — don't double it up.
                    if (e.relatedTarget instanceof HTMLElement && e.relatedTarget.classList.contains('input-region-confirm')) return;
                    suppressStageClickRef.current = true;
                    submitInputRegion(r);
                  }}
                />
                {confirmTriggers.includes('button') && (
                  <button
                    type="button"
                    className="input-region-confirm"
                    style={{
                      left: metrics.offsetX + (r.rect.x + r.rect.w) * metrics.drawW + 6,
                      top: metrics.offsetY + r.rect.y * metrics.drawH,
                      height: r.rect.h * metrics.drawH,
                    }}
                    title="Bevestigen (Enter)"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); submitInputRegion(r); }}
                  >
                    ✓
                  </button>
                )}
              </div>
            );
          })}

        {/* Instruction: demo the expected value being typed into the field. */}
        {isInstruction && typingRegion && (
          <div
            className="input-region-field instruction-typing"
            style={{
              left: metrics.offsetX + typingRegion.rect.x * metrics.drawW,
              top: metrics.offsetY + typingRegion.rect.y * metrics.drawH,
              width: typingRegion.rect.w * metrics.drawW,
              height: typingRegion.rect.h * metrics.drawH,
            }}
          >
            <span>{typedText}</span>
            <span className="typing-caret" />
          </div>
        )}

        {/* Animated cursor (instruction mode only) */}
        {isInstruction && metrics.drawW > 0 && !paused && (
          <div
            // A hover region isn't clicked at all — the ripple would
            // misleadingly suggest one happened, so it's suppressed there.
            className={`virtual-cursor ${clicking && demoClickType !== 'hover' ? 'clicking' : ''}`}
            style={{ left: cursorPos.x, top: cursorPos.y }}
          />
        )}
        {isInstruction && metrics.drawW > 0 && !paused && clicking && demoClickType !== 'left' && (
          <div className="click-type-badge" style={{ left: cursorPos.x + 22, top: cursorPos.y + 18 }}>
            {demoClickType === 'double' ? 'dubbelklik' : demoClickType === 'right' ? 'rechtsklik' : 'muis erover houden'}
          </div>
        )}

        {/* Pause/resume indicator — click anywhere or press space to toggle. */}
        {isInstruction && paused && (
          <div className="pause-overlay">⏸</div>
        )}
        {isInstruction && playFlash && (
          <div className="pause-overlay play-flash">▶</div>
        )}

        {/* Feedback balloon — floats over the stage, on whichever side (top
            or bottom) doesn't sit on top of this step's regions. */}
        {feedbackKind !== 'idle' && (
          <div className={`feedback-balloon ${balloonPosition} ${
            feedbackKind === 'wrong' ? 'error' : feedbackKind === 'corrective' ? 'warn' : 'ok'
          }`}>
            <div className="feedback-icon">
              {feedbackKind === 'good' && '✓'}
              {feedbackKind === 'wrong' && (runner.feedback.kind === 'wrong' && runner.feedback.stage === 2 ? '!' : '✕')}
              {feedbackKind === 'corrective' && '↳'}
            </div>
            <div className="feedback-balloon-text">{feedbackText}</div>
          </div>
        )}

        {/* A `hover`-type region needs the window to actually be the active
            OS-level application — macOS never delivers mousemove to a
            visible-but-non-key window, and window.focus() from a script
            can't force that the way a real click/app-switch can. Told once
            per step so it doesn't nag on steps that don't need it. */}
        {!isInstruction && hasHoverRegion && (
          <div className="hover-focus-hint">
            🖱️ Reageert bewegen niet meteen? Klik één keer op dit venster.
          </div>
        )}
      </div>
    </div>
  );
}
